import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  BitemporalResponse,
  VersionHistoryResponse,
  BitemporalMetadata,
  formatDateTime,
  formatDateTimeForInput,
  getCurrentDateTime,
} from '../../services/bitemporalApi';

interface BitemporalViewerProps<T> {
  entityId: string;
  entityType: string;
  onGetHistory: (entityId: string) => Promise<VersionHistoryResponse<T>>;
  onGetAtTime: (entityId: string, pitTime: string) => Promise<BitemporalResponse<T>>;
  renderData: (data: T) => React.ReactNode;
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bitemporal-tabpanel-${index}`}
      aria-labelledby={`bitemporal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function BitemporalViewer<T>({
  entityId,
  entityType,
  onGetHistory,
  onGetAtTime,
  renderData,
  open,
  onClose,
}: BitemporalViewerProps<T>) {
  const [tabValue, setTabValue] = useState(0);
  
  // History tab state
  const [history, setHistory] = useState<VersionHistoryResponse<T> | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  
  // Point-in-time tab state
  const [pitTime, setPitTime] = useState<string>(formatDateTimeForInput(getCurrentDateTime()));
  const [pitData, setPitData] = useState<BitemporalResponse<T> | null>(null);
  const [pitLoading, setPitLoading] = useState(false);
  const [pitError, setPitError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const data = await onGetHistory(entityId);
      setHistory(data);
    } catch (err) {
      console.error('Failed to load history:', err);
      setHistoryError('Failed to load version history');
    } finally {
      setHistoryLoading(false);
    }
  }, [entityId, onGetHistory]);

  useEffect(() => {
    if (open && tabValue === 0) {
      loadHistory();
    }
  }, [open, tabValue, loadHistory]);

  const loadPitData = async () => {
    try {
      setPitLoading(true);
      setPitError(null);
      const data = await onGetAtTime(entityId, new Date(pitTime).toISOString());
      setPitData(data);
    } catch (err) {
      console.error('Failed to load point-in-time data:', err);
      setPitError('Failed to load data at specified time');
    } finally {
      setPitLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderBitemporalMetadata = (metadata: BitemporalMetadata) => (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Bitemporal Metadata
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <Typography variant="body2">
          <strong>Entity ID:</strong> {metadata.entity_id}
        </Typography>
        <Typography variant="body2">
          <strong>Record ID:</strong> {metadata.record_id}
        </Typography>
        <Typography variant="body2">
          <strong>Version:</strong> {metadata.version}
        </Typography>
        <Typography variant="body2">
          <strong>Current:</strong> 
          <Chip 
            label={metadata.is_current ? 'Yes' : 'No'} 
            size="small" 
            color={metadata.is_current ? 'success' : 'default'}
            sx={{ ml: 1 }}
          />
        </Typography>
        <Typography variant="body2">
          <strong>Transaction Start:</strong> {formatDateTime(metadata.transaction_time_start)}
        </Typography>
        <Typography variant="body2">
          <strong>Transaction End:</strong> {metadata.transaction_time_end ? formatDateTime(metadata.transaction_time_end) : 'Current'}
        </Typography>
        <Typography variant="body2">
          <strong>Valid Start:</strong> {formatDateTime(metadata.valid_time_start)}
        </Typography>
        <Typography variant="body2">
          <strong>Valid End:</strong> {metadata.valid_time_end ? formatDateTime(metadata.valid_time_end) : 'Ongoing'}
        </Typography>
        {metadata.created_by && (
          <Typography variant="body2">
            <strong>Created By:</strong> {metadata.created_by}
          </Typography>
        )}
        {metadata.updated_by && (
          <Typography variant="body2">
            <strong>Updated By:</strong> {metadata.updated_by}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderVersionCard = (version: BitemporalResponse<T>, _index: number) => (
    <Accordion key={version.bitemporal_metadata.record_id} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography variant="h6">
            Version {version.bitemporal_metadata.version}
          </Typography>
          <Box sx={{ ml: 2, display: 'flex', gap: 1 }}>
            <Chip 
              label={version.bitemporal_metadata.is_current ? 'Current' : 'Historical'} 
              size="small" 
              color={version.bitemporal_metadata.is_current ? 'primary' : 'default'}
            />
            <Chip 
              label={formatDateTime(version.bitemporal_metadata.transaction_time_start)} 
              size="small" 
              variant="outlined"
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Data at this version:
          </Typography>
          {renderData(version.data)}
          {renderBitemporalMetadata(version.bitemporal_metadata)}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Bitemporal View: {entityType} {entityId}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab 
              label="Version History" 
              icon={<HistoryIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Point-in-Time Query" 
              icon={<ScheduleIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {historyLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading version history...
              </Typography>
            </Box>
          )}

          {historyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {historyError}
            </Alert>
          )}

          {history && !historyLoading && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Version History ({history.total_versions} versions)
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Entity ID: {history.entity_id}
              </Typography>
              
              {history.versions.map((version, index) => (
                <div key={version.bitemporal_metadata.record_id}>
                  {renderVersionCard(version, index)}
                </div>
              ))}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Query Data at Specific Point in Time
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Enter a date and time to see how this {entityType.toLowerCase()} appeared at that moment.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField
                label="Point in Time"
                type="datetime-local"
                value={pitTime}
                onChange={(e) => setPitTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={loadPitData}
                disabled={pitLoading}
                startIcon={<VisibilityIcon />}
              >
                Query
              </Button>
            </Box>

            {pitLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Loading data at specified time...
                </Typography>
              </Box>
            )}

            {pitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {pitError}
              </Alert>
            )}

            {pitData && !pitLoading && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Data at {formatDateTime(pitTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Query executed at: {formatDateTime(pitData.query_time)}
                </Typography>
                
                {renderData(pitData.data)}
                {renderBitemporalMetadata(pitData.bitemporal_metadata)}
              </Paper>
            )}
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}